import { Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { OSStep } from "@/molecules/OSStep"
import { DeployTab } from "@/pages/DeploymentsPage/components/molecules/DeployTab"
import { AGENT_DEPLOYMENTS } from "@/pages/DeploymentsPage/const"

import type { AgentPlatformTabProps } from "./AgentPlatformTab.types"

const isSensorMethod = (key: string) =>
  AGENT_DEPLOYMENTS.some((deployment) => deployment === key)

// An AI agent platform (AgentCore, Foundry): how Kodem reaches the agent is
// selected first, then the selected method's steps are shown
export const AgentPlatformTab = ({
  platform,
  methods,
  defaultMethod,
  onMethodChange,
  children
}: AgentPlatformTabProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "deployments" })
  const steps = Object.fromEntries(
    methods.map(({ key, render }) => [key, render])
  )

  const {
    items,
    isWindows: _isWindows,
    ...rest
  } = OSStep({
    options: methods.map(({ key, label }) => ({ label, value: key })),
    defaultValue: defaultMethod,
    onOSPlatformChange: onMethodChange,
    placeholder: t("agentPlatform.selectDeployment.placeholder"),
    waitingTitle: t("agentPlatform.selectDeployment.waiting"),
    getSteps: ({ OSPlatform: method }) => steps[method]?.() ?? [],
    // The sensors' support note; a method without sensors has none
    getMessage: ({ OSPlatform: method }) =>
      isSensorMethod(method) && (
        <span>
          <Text>
            {t(`${platform.i18nKey}.alert.supported`)}
            <br />
            {t("kubernetes.alert.text")}
          </Text>
          <br />
          <b>{t("kubernetes.alert.note")}</b>
        </span>
      )
  })

  // The platforms are not documented yet
  return (
    <DeployTab steps={items} {...rest} hideReadMoreLink>
      {children}
    </DeployTab>
  )
}
